// Pilot mode: the setting, its calendar, and the team metrics it compares.
//
// The pilot (the founder's terms): 45 days. Days 1 to 14 are the baseline:
// BossDrop watches only and sends the weekly report, no nudges and no
// escalations (the server enforces this). Days 15 to 45 BossDrop runs fully, in
// Automated: the server switches the workspace on day 15, once, and an admin can
// send any automation back to approval after that.
// Then the readout against the two-week baseline; when the result is unclear
// the pilot is extended two weeks (45 -> 59 days), once.
//
// settings.pilot (a top-level workspace setting, saved like taskWriteBackend):
//   { startDate: "YYYY-MM-DD", lengthDays: 45 | 59, baselineDays: 14,
//     kpis: [2 or 3 team metric keys], endedAt: ISO string | null }
//
// The server (GET /api/pilot/status, backend server/pilot/) owns the phase:
// pilotStatus() there is what gates the baseline, and its answer wins here.
// The client calendar below mirrors it for the moments before that answer.
// Days are calendar days in the workspace's time zone; every date is a
// "YYYY-MM-DD" string and all arithmetic is on calendar days (UTC day
// numbers), never on 24 h multiples, so a DST week keeps seven days.
import { toYMD } from "./kpi";

export const PILOT_LENGTH_DAYS = 45;
export const PILOT_EXTENDED_LENGTH_DAYS = 59;
export const PILOT_BASELINE_DAYS = 14;
export const PILOT_MIN_KPIS = 2;
export const PILOT_MAX_KPIS = 3;

export const PILOT_PHASES = ["scheduled", "baseline", "running", "readout", "ended"];
export const PILOT_PHASE_LABELS = {
  scheduled: "Scheduled",
  baseline: "Baseline",
  running: "Running",
  readout: "Readout",
  ended: "Ended",
};

/**
 * The team metrics a pilot can compare: PILOT_METRICS in the backend's
 * server/pilot/pilotMetrics.js, same keys, labels, directions and formats, in
 * the same order. The payload's own label/direction/format win once it loads;
 * this list is for the selector before it does. direction "neutral" is never
 * coloured. format "percent" values are 0 to 1 fractions.
 */
export const PILOT_TEAM_METRICS = [
  { key: "open_tasks", label: "Open tasks", direction: "neutral", format: "number" },
  { key: "overdue_open", label: "Overdue open tasks", direction: "lower", format: "number" },
  { key: "overdue_share", label: "Share of open tasks overdue", direction: "lower", format: "percent" },
  {
    key: "owner_and_due_share",
    label: "Open tasks with an owner and a due date",
    direction: "higher",
    format: "percent",
  },
  {
    key: "stale_share",
    label: "Open tasks with no update in 7+ days",
    direction: "lower",
    format: "percent",
  },
  { key: "blocked_open", label: "Blocked open tasks", direction: "lower", format: "number" },
  { key: "on_time_rate", label: "Tasks completed on time", direction: "higher", format: "percent" },
  { key: "throughput", label: "Tasks completed per week", direction: "higher", format: "number" },
];

export const PILOT_DEFAULT_KPIS = ["on_time_rate", "overdue_share", "owner_and_due_share"];

const METRIC_KEYS = new Set(PILOT_TEAM_METRICS.map((m) => m.key));
// Keys an earlier build of the hub saved (before the backend named them).
const LEGACY_KPI_KEYS = {
  onTimeRate: "on_time_rate",
  overdueOpen: "overdue_open",
  overdueShare: "overdue_share",
  ownerAndDueShare: "owner_and_due_share",
  staleShare: "stale_share",
  blockedOpen: "blocked_open",
  throughputPerWeek: "throughput",
};

/* ---------------- Calendar days ---------------- */
const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function dayNumber(ymd) {
  const m = YMD_RE.exec(String(ymd || ""));
  if (!m) return NaN;
  const n = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  // Reject dates that roll over ("2026-02-30").
  return new Date(n).toISOString().slice(0, 10) === ymd ? n / 86400000 : NaN;
}

export function isYmd(value) {
  return Number.isFinite(dayNumber(value));
}

/** `ymd` plus `n` calendar days. */
export function addCalendarDays(ymd, n) {
  const d = dayNumber(ymd);
  if (!Number.isFinite(d)) return ymd;
  return new Date((d + n) * 86400000).toISOString().slice(0, 10);
}

/** Calendar days from `from` to `to` (0 on the same day). */
export function daysBetween(from, to) {
  return dayNumber(to) - dayNumber(from);
}

/** "Oct 20" for a YYYY-MM-DD, whatever zone the browser is in. */
export function formatPilotDate(ymd) {
  const d = dayNumber(ymd);
  if (!Number.isFinite(d)) return "";
  return new Date(d * 86400000).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function usableTimeZone(tz) {
  if (!tz || typeof tz !== "string") return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/**
 * The workspace's time zone as the hub can see it, in the server's order
 * (resolvePollTimeZone in backend/server/automationWorker.js):
 * slackPrefs.tz, then business hours' zone, else UTC.
 */
export function workspaceTimeZone(slackPrefs) {
  const prefs = slackPrefs && typeof slackPrefs === "object" ? slackPrefs : {};
  const bh =
    prefs.businessHours && typeof prefs.businessHours === "object"
      ? prefs.businessHours
      : {};
  return [prefs.tz, bh.tz].find(usableTimeZone) || "Etc/UTC";
}

/** Today's date (YYYY-MM-DD) in `timeZone`. */
export function todayInZone(now = new Date(), timeZone) {
  if (usableTimeZone(timeZone)) {
    const parts = {};
    for (const p of new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).formatToParts(now)) {
      parts[p.type] = p.value;
    }
    if (parts.year && parts.month && parts.day) {
      return `${parts.year}-${parts.month}-${parts.day}`;
    }
  }
  return toYMD(now);
}

/* ---------------- The setting ---------------- */
/**
 * 2 or 3 distinct metric keys (the backend's), older camelCase keys
 * migrated; anything else is the default selection.
 */
export function pilotKpisShape(value) {
  const out = [];
  for (const raw of Array.isArray(value) ? value : []) {
    const text = String(raw ?? "").trim();
    const k = LEGACY_KPI_KEYS[text] || text;
    if (!METRIC_KEYS.has(k) || out.includes(k)) continue;
    out.push(k);
    if (out.length >= PILOT_MAX_KPIS) break;
  }
  return out.length >= PILOT_MIN_KPIS ? out : [...PILOT_DEFAULT_KPIS];
}

/** settings.pilot as stored, or null when there is no (valid) pilot. */
export function pilotShape(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const startDate = String(value.startDate || "").trim();
  if (!isYmd(startDate)) return null;
  const lengthDays =
    Number(value.lengthDays) === PILOT_EXTENDED_LENGTH_DAYS
      ? PILOT_EXTENDED_LENGTH_DAYS
      : PILOT_LENGTH_DAYS;
  const endedAt =
    typeof value.endedAt === "string" && !Number.isNaN(Date.parse(value.endedAt))
      ? value.endedAt
      : null;
  return {
    startDate,
    lengthDays,
    baselineDays: PILOT_BASELINE_DAYS,
    kpis: pilotKpisShape(value.kpis),
    endedAt,
  };
}

/** A new pilot starting on `startDate` (keeps a previous KPI choice). */
export function startPilot(startDate, previous = null) {
  return {
    startDate,
    lengthDays: PILOT_LENGTH_DAYS,
    baselineDays: PILOT_BASELINE_DAYS,
    kpis: pilotKpisShape(previous?.kpis),
    endedAt: null,
  };
}

/** The pilot two weeks longer (45 -> 59 days), once. */
export function extendPilot(pilot) {
  const p = pilotShape(pilot);
  if (!p || p.lengthDays !== PILOT_LENGTH_DAYS) return p;
  return { ...p, lengthDays: PILOT_EXTENDED_LENGTH_DAYS };
}

/** The pilot ended now: settings stay (for the record), endedAt is set. */
export function endPilot(pilot, now = new Date()) {
  const p = pilotShape(pilot);
  return p ? { ...p, endedAt: now.toISOString() } : p;
}

/**
 * The KPI selection with `key` toggled, kept to 2 or 3: it returns `kpis`
 * unchanged when the toggle would leave fewer than 2 or more than 3.
 */
export function togglePilotKpi(kpis, key) {
  const list = Array.isArray(kpis) ? kpis : [];
  if (list.includes(key)) {
    return list.length <= PILOT_MIN_KPIS ? list : list.filter((k) => k !== key);
  }
  return list.length >= PILOT_MAX_KPIS ? list : [...list, key];
}

/* ---------------- Where the pilot is ---------------- */
function stateFor(p, { phase, day, baselineEndsOn, endsOn, source }) {
  return {
    phase,
    // Shown as "Day N of 45": it stops at the last day through the readout.
    day: Number.isFinite(day) ? Math.max(0, Math.min(day, p.lengthDays)) : 0,
    lengthDays: p.lengthDays,
    baselineDays: p.baselineDays,
    startDate: p.startDate,
    baselineEndsOn,
    runStartsOn: addCalendarDays(baselineEndsOn, 1),
    endsOn,
    canExtend:
      p.lengthDays === PILOT_LENGTH_DAYS &&
      (phase === "running" || phase === "readout"),
    source,
  };
}

/**
 * The pilot's calendar and phase on `today`, computed here the way the
 * server's pilotStatus() does:
 *   { phase, day, lengthDays, baselineDays, startDate, baselineEndsOn,
 *     runStartsOn, endsOn, canExtend, source: "client" }
 * Day 1 is startDate. Baseline: days 1 to 14. Running: day 15 to the last day
 * (45, or 59 when extended). Readout: after the last day, until it is ended.
 */
export function pilotState(pilot, today) {
  const p = pilotShape(pilot);
  if (!p) return null;
  const rawDay = daysBetween(p.startDate, today) + 1;
  let phase;
  if (p.endedAt) phase = "ended";
  else if (!(rawDay >= 1)) phase = "scheduled";
  else if (rawDay <= p.baselineDays) phase = "baseline";
  else if (rawDay <= p.lengthDays) phase = "running";
  else phase = "readout";
  return stateFor(p, {
    phase,
    day: rawDay,
    baselineEndsOn: addCalendarDays(p.startDate, p.baselineDays - 1),
    endsOn: addCalendarDays(p.startDate, p.lengthDays - 1),
    source: "client",
  });
}

/**
 * True when a status payload (normalizePilotStatus) describes THIS pilot
 * setting: same start, same length, ended or not. Right after Start, Extend
 * or End the server still answers for the setting it last saved; that answer
 * is stale and is not used.
 */
export function statusMatchesPilot(status, pilot) {
  const p = pilotShape(pilot);
  if (!p || !status) return false;
  return (
    status.startDate === p.startDate &&
    status.lengthDays === p.lengthDays &&
    !!status.endedAt === !!p.endedAt
  );
}

/**
 * SERVER ADAPTER: the server's phase and day (from normalizePilotStatus) in
 * pilotState()'s shape, source "server"; null when there is no usable,
 * current answer (callers then fall back to pilotState()).
 */
export function pilotStateFromServer(status, pilot) {
  const p = pilotShape(pilot);
  if (!p || !statusMatchesPilot(status, p)) return null;
  const phase = status.phase === "off" && status.endedAt ? "ended" : status.phase;
  if (!PILOT_PHASES.includes(phase)) return null;
  return stateFor(p, {
    phase,
    day: phase === "scheduled" ? 0 : Number(status.day),
    baselineEndsOn: status.baselineEndsOn || addCalendarDays(p.startDate, p.baselineDays - 1),
    endsOn: status.endsOn || addCalendarDays(p.startDate, p.lengthDays - 1),
    source: "server",
  });
}

/** The server's state when it has a current one, else the client's. */
export function resolvePilotState({ pilot, today, server = null }) {
  const p = pilotShape(pilot);
  if (!p) return null;
  return pilotStateFromServer(server, p) || pilotState(p, today);
}

/* ---------------- The server's status and team metrics ---------------- */
// The ONE place the hub reads the pilot from the server (backend
// server/pilot/pilotStore.js pilotStatusPayload): workspace-member access,
// the session's own workspace, no-store.
export const PILOT_STATUS_ROUTE = "/api/pilot/status";

/**
 * GET /api/pilot/status, normalized (normalizePilotStatus), or null when it
 * cannot be read. `fetchImpl` is injectable for tests.
 */
export async function fetchPilotMetrics({ apiBase = "", fetchImpl } = {}) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return null;
  try {
    const base = String(apiBase || "").replace(/\/+$/, "");
    const r = await doFetch(`${base}${PILOT_STATUS_ROUTE}`, {
      credentials: "include",
      cache: "no-store",
    });
    if (!r || !r.ok) return null;
    return normalizePilotStatus(await r.json());
  } catch {
    return null;
  }
}

/**
 * What BossDrop has learned so far, for the pilot's readout (GET
 * /api/memory/summary): { stats, highlights }, or null when it cannot be read.
 */
export async function fetchMemorySummary({ apiBase = "", fetchImpl } = {}) {
  const doFetch = fetchImpl || (typeof fetch === "function" ? fetch : null);
  if (!doFetch) return null;
  try {
    const base = String(apiBase || "").replace(/\/+$/, "");
    const r = await doFetch(`${base}/api/memory/summary`, {
      credentials: "include",
      cache: "no-store",
    });
    if (!r || !r.ok) return null;
    const json = await r.json();
    return {
      stats: json?.stats && typeof json.stats === "object" ? json.stats : {},
      highlights: Array.isArray(json?.highlights) ? json.highlights.filter((h) => h && h.text) : [],
    };
  } catch {
    return null;
  }
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

const ymdOrNull = (v) => (isYmd(v) ? v : null);

/**
 * The status payload,
 *   { ok, pilot: { phase, day, startDate, baselineEndsOn, endsOn, lengthDays,
 *                  baselineDays, today, timeZone, endedAt, endedOn,
 *                  actionsHeld, collecting },
 *     metrics: null | { windows,
 *                       daysCollected: { baseline, run, readout,
 *                                        partial: { baseline, run, readout } },
 *                       metrics: [{ key, label, kind, direction, format,
 *                                   baseline, run, prePilot }],
 *                       counts, prePilot } }
 * flattened to
 *   { phase, day, startDate, baselineEndsOn, endsOn, lengthDays, today,
 *     timeZone, endedAt, actionsHeld, collecting,
 *     collected: { baselineDays, runDays, readoutDays, partialDays },
 *     metrics: [{ key, label, direction, format, prePilot, baseline, run }] }
 * or null when it is not that payload.
 */
export function normalizePilotStatus(payload) {
  const pilot = payload?.pilot;
  if (!payload || payload.ok === false || !pilot || typeof pilot !== "object") {
    return null;
  }
  const phase = String(pilot.phase || "").toLowerCase();
  if (!["off", "scheduled", "baseline", "running", "readout"].includes(phase)) return null;
  const m = payload.metrics && typeof payload.metrics === "object" ? payload.metrics : null;
  const days = m?.daysCollected && typeof m.daysCollected === "object" ? m.daysCollected : {};
  const catalog = Object.fromEntries(PILOT_TEAM_METRICS.map((x) => [x.key, x]));
  const metrics = [];
  for (const raw of Array.isArray(m?.metrics) ? m.metrics : []) {
    const key = String(raw?.key || "").trim();
    if (!key || metrics.some((x) => x.key === key)) continue;
    const known = catalog[key] || {};
    metrics.push({
      key,
      label: String(raw.label || known.label || key),
      direction: ["higher", "lower", "neutral"].includes(raw.direction)
        ? raw.direction
        : known.direction || "neutral",
      format: raw.format === "percent" || raw.format === "number"
        ? raw.format
        : known.format || "number",
      prePilot: numOrNull(raw.prePilot),
      baseline: numOrNull(raw.baseline),
      run: numOrNull(raw.run),
    });
  }
  const lengthDays = numOrNull(pilot.lengthDays);
  const count = (v) => Math.max(0, Math.round(numOrNull(v) || 0));
  // Days a task tool failed all day: snapshotted, but left out of the state
  // averages (the backend's daysCollected.partial, per phase).
  const partial =
    days.partial && typeof days.partial === "object" ? days.partial : {};
  return {
    phase,
    day: numOrNull(pilot.day),
    startDate: ymdOrNull(pilot.startDate),
    baselineEndsOn: ymdOrNull(pilot.baselineEndsOn),
    endsOn: ymdOrNull(pilot.endsOn),
    lengthDays:
      lengthDays === PILOT_EXTENDED_LENGTH_DAYS || lengthDays === PILOT_LENGTH_DAYS
        ? lengthDays
        : null,
    today: ymdOrNull(pilot.today),
    timeZone: typeof pilot.timeZone === "string" ? pilot.timeZone : null,
    endedAt: typeof pilot.endedAt === "string" && pilot.endedAt ? pilot.endedAt : null,
    actionsHeld: pilot.actionsHeld === true,
    collecting: pilot.collecting === true,
    collected: {
      baselineDays: count(days.baseline),
      runDays: count(days.run),
      readoutDays: count(days.readout),
      partialDays:
        count(partial.baseline) + count(partial.run) + count(partial.readout),
    },
    metrics,
  };
}

/** The metrics to offer: the payload's when loaded, else the catalog. */
export function pilotMetricOptions(status) {
  const fromPayload = Array.isArray(status?.metrics) ? status.metrics : [];
  return fromPayload.length ? fromPayload : PILOT_TEAM_METRICS;
}

const NULL_TEXT = "—";

const trimNumber = (n) =>
  Number.isInteger(n) ? String(n) : (Math.round(n * 10) / 10).toFixed(1);

/** A metric value for display; null shows "—", like the KPI tiles. */
export function formatMetricValue(value, format) {
  if (value == null || !Number.isFinite(Number(value))) return NULL_TEXT;
  const n = Number(value);
  if (format === "percent") return `${(n * 100).toFixed(1)}%`;
  return trimNumber(n);
}

/**
 * Change from baseline to run: { text, tone: "good" | "bad" | "neutral" }.
 * Percent metrics change in points, the others in their own number. The tone
 * follows the metric's direction ("neutral" is never coloured); "—" when
 * either side is missing.
 */
export function metricChange(metric) {
  const b = metric?.baseline;
  const r = metric?.run;
  if (b == null || r == null || !Number.isFinite(b) || !Number.isFinite(r)) {
    return { text: NULL_TEXT, tone: "neutral" };
  }
  const percent = metric.format === "percent";
  const diff = percent ? (r - b) * 100 : r - b;
  const rounded = Math.round(diff * 10) / 10;
  if (rounded === 0) {
    return { text: percent ? "→ 0.0 pts" : "→ 0", tone: "neutral" };
  }
  const arrow = rounded > 0 ? "↑" : "↓";
  const sign = rounded > 0 ? "+" : "−";
  const abs = Math.abs(rounded);
  const text = `${arrow} ${sign}${percent ? `${abs.toFixed(1)} pts` : trimNumber(abs)}`;
  if (metric.direction !== "higher" && metric.direction !== "lower") {
    return { text, tone: "neutral" };
  }
  const better = metric.direction === "lower" ? rounded < 0 : rounded > 0;
  return { text, tone: better ? "good" : "bad" };
}

/**
 * The window "since day 15" covers for the activity line: day 15 to today, or
 * to the last day once the pilot is over. null before day 15.
 */
export function pilotRunWindow(state, today) {
  if (!state || (state.phase !== "running" && state.phase !== "readout")) {
    return null;
  }
  const end = daysBetween(state.endsOn, today) > 0 ? state.endsOn : today;
  return { start: state.runStartsOn, end };
}
